import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Switch,
  FormControlLabel,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DecimalField from '../shared/DecimalField';
import { useUserStore } from '../../store';
import AddPartDialog from '../maintenances/AddPartDialog';
import AddJobDialog from '../maintenances/AddJobDialog';
import CopyFromMaintenanceDialog from '../maintenances/CopyFromMaintenanceDialog';
import ReplacementCandidatesDialog from './ReplacementCandidatesDialog';

// DRF error payloads nest strings, arrays and objects; flatten to a list of messages.
function flattenErrors(value) {
  if (!value) return [];
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value.flatMap(flattenErrors);
  if (typeof value === 'object') return Object.values(value).flatMap(flattenErrors);
  return [String(value)];
}

// UUID v4 for alternative-group ids
function uuid4() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}


function ServicePackageEditView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentOrganization } = useUserStore();
  const orgId = currentOrganization?.organization_id;

  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  // Header form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [estimatedLaborHours, setEstimatedLaborHours] = useState('');

  // Rows — each row is registry-backed: { _key, id?, part: {...nested read}, quantity, alternative_group, is_preferred, sort_order }
  const [jobs, setJobs] = useState([]);
  const [parts, setParts] = useState([]);

  // Picker dialogs
  const [addPartDialogOpen, setAddPartDialogOpen] = useState(false);
  // When altSourceKey is set we're adding an alternative to the target row.
  const [altSourceKey, setAltSourceKey] = useState(null);
  const [addJobDialogOpen, setAddJobDialogOpen] = useState(false);
  const [copyFromDialogOpen, setCopyFromDialogOpen] = useState(false);
  // A copied row whose part/job was deleted from the register: the picker replaces
  // that row instead of appending. partDialogSearch pre-fills the picker with the
  // original code so the replacement is one click away.
  const [replacePartKey, setReplacePartKey] = useState(null);
  const [replaceJobKey, setReplaceJobKey] = useState(null);
  const [partDialogSearch, setPartDialogSearch] = useState('');
  // Replaced row whose alternatives (register parts, catalogue items) are being chosen from.
  const [candidatesRowKey, setCandidatesRowKey] = useState(null);
  // Per-row server validation errors from the last save attempt, keyed by row _key.
  const [rowErrors, setRowErrors] = useState({ jobs: {}, parts: {} });

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`/api/service-packages/${id}/`, {
          params: { organization: orgId },
        });
        if (cancelled) return;
        const data = response.data;
        setName(data.name || '');
        setDescription(data.description || '');
        setIsActive(data.is_active);
        setEstimatedLaborHours(data.estimated_labor_hours ?? '');
        setJobs((data.jobs || []).map((j) => ({ ...j, _key: `db-${j.id}` })));
        setParts((data.parts || []).map((p) => ({ ...p, _key: `db-${p.id}` })));
      } catch {
        setSnackbar({ severity: 'error', message: t('huoltopaketit.errorLoading') });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isNew, orgId, t]);

  // --- computed sum of hourly job hours for the hint line --------------
  const computedJobHours = useMemo(() => {
    let total = 0;
    let any = false;
    jobs.forEach((row) => {
      if (!row.job || row.job.price_type !== 'hourly') return;
      const raw = row.quantity ?? 1;
      const val = parseFloat(String(raw).replace(',', '.'));
      if (!Number.isNaN(val)) { total += val; any = true; }
    });
    return any ? total.toFixed(2) : null;
  }, [jobs]);

  // --- jobs helpers -----------------------------------------------------
  const handleJobPicked = (catalogJob) => {
    const job = {
      id: catalogJob.id,
      name: catalogJob.name,
      price_type: catalogJob.price_type,
      price: catalogJob.price,
      vat_rate: catalogJob.vat_rate,
    };
    if (replaceJobKey) {
      setJobs((prev) => prev.map((row) => (
        row._key === replaceJobKey
          ? { ...row, job, job_id: catalogJob.id, _status: undefined, _original: undefined }
          : row
      )));
    } else {
      setJobs((prev) => [
        ...prev,
        {
          _key: `new-${Date.now()}-${Math.random()}`,
          job,
          job_id: catalogJob.id,
          quantity: '',
          description_override: '',
          is_optional: false,
          sort_order: prev.length,
        },
      ]);
    }
    setAddJobDialogOpen(false);
    setReplaceJobKey(null);
  };

  const updateJob = (key, patch) => {
    setJobs((prev) => prev.map((j) => (j._key === key ? { ...j, ...patch } : j)));
  };

  const removeJob = (key) => {
    setJobs((prev) => prev.filter((j) => j._key !== key).map((j, i) => ({ ...j, sort_order: i })));
  };

  const moveJob = (key, delta) => {
    setJobs((prev) => {
      const idx = prev.findIndex((j) => j._key === key);
      const next = [...prev];
      const [item] = next.splice(idx, 1);
      next.splice(Math.max(0, Math.min(next.length, idx + delta)), 0, item);
      return next.map((j, i) => ({ ...j, sort_order: i }));
    });
  };

  // --- parts helpers ----------------------------------------------------
  const handlePartPicked = (catalogPart) => {
    // AI extraction paths call onPartCreated() without arguments after importing
    // to the catalog. Ignore — user can then pick the part via search.
    if (!catalogPart) return;
    // catalogPart shape from AddPartDialog: full Part row (response from /api/parts/)
    const picked = {
      id: catalogPart.id,
      name: catalogPart.name,
      manufacturer_code: catalogPart.manufacturer_code || '',
      manufacturer_name: catalogPart.manufacturer_name || '',
      unit: catalogPart.unit || 'kpl',
      primary_oe_number: catalogPart.primary_identifier_code || '',
    };
    if (replacePartKey) {
      setParts((prev) => prev.map((row) => (
        row._key === replacePartKey
          ? { ...row, part: picked, part_id: catalogPart.id, _status: undefined, _original: undefined }
          : row
      )));
      setAddPartDialogOpen(false);
      setReplacePartKey(null);
      setPartDialogSearch('');
      return;
    }
    const newKey = `new-${Date.now()}-${Math.random()}`;
    setParts((prev) => {
      // Resolve alternative group based on whether we're adding an alternative
      // to an existing row or a standalone row.
      let alternative_group = null;
      let next = prev;
      let insertAt = prev.length;
      if (altSourceKey) {
        const sourceIdx = prev.findIndex((p) => p._key === altSourceKey);
        if (sourceIdx >= 0) {
          const source = prev[sourceIdx];
          if (source.alternative_group) {
            alternative_group = source.alternative_group;
          } else {
            alternative_group = uuid4();
            next = prev.map((p) =>
              p._key === altSourceKey
                ? { ...p, alternative_group, is_preferred: true }
                : p
            );
          }
          insertAt = sourceIdx + 1;
        }
      }
      const row = {
        _key: newKey,
        part: picked,
        part_id: catalogPart.id,
        quantity: '1',
        alternative_group,
        is_preferred: false,
        sort_order: insertAt,
      };
      const result = [...next];
      result.splice(insertAt, 0, row);
      return result.map((p, i) => ({ ...p, sort_order: i }));
    });
    setAddPartDialogOpen(false);
    setAltSourceKey(null);
  };

  const updatePart = (key, patch) => {
    setParts((prev) => prev.map((p) => (p._key === key ? { ...p, ...patch } : p)));
  };

  const markPreferred = (key) => {
    setParts((prev) => {
      const row = prev.find((p) => p._key === key);
      if (!row || !row.alternative_group) return prev;
      return prev.map((p) => (
        p.alternative_group === row.alternative_group
          ? { ...p, is_preferred: p._key === key }
          : p
      ));
    });
  };

  const removeFromAltGroup = (key) => {
    setParts((prev) => {
      const row = prev.find((p) => p._key === key);
      if (!row || !row.alternative_group) return prev;
      const group = row.alternative_group;
      const siblings = prev.filter((p) => p.alternative_group === group && p._key !== key);
      let next = prev.map((p) => (
        p._key === key
          ? { ...p, alternative_group: null, is_preferred: false }
          : p
      ));
      if (row.is_preferred && siblings.length > 0) {
        const promote = siblings[0]._key;
        next = next.map((p) => (p._key === promote ? { ...p, is_preferred: true } : p));
      }
      if (siblings.length === 1) {
        const lastKey = siblings[0]._key;
        next = next.map((p) => (
          p._key === lastKey
            ? { ...p, alternative_group: null, is_preferred: false }
            : p
        ));
      }
      return next;
    });
  };

  const removePart = (key) => {
    setParts((prev) => {
      const row = prev.find((p) => p._key === key);
      if (!row) return prev;
      const group = row.alternative_group;
      let after = prev.filter((p) => p._key !== key);
      if (group) {
        const siblings = after.filter((p) => p.alternative_group === group);
        if (row.is_preferred && siblings.length > 0) {
          after = after.map((p) => (p._key === siblings[0]._key ? { ...p, is_preferred: true } : p));
        }
        if (siblings.length === 1) {
          after = after.map((p) => (
            p._key === siblings[0]._key
              ? { ...p, alternative_group: null, is_preferred: false }
              : p
          ));
        }
      }
      return after.map((p, i) => ({ ...p, sort_order: i }));
    });
  };

  const movePart = (key, delta) => {
    setParts((prev) => {
      const idx = prev.findIndex((p) => p._key === key);
      const next = [...prev];
      const [item] = next.splice(idx, 1);
      next.splice(Math.max(0, Math.min(next.length, idx + delta)), 0, item);
      return next.map((p, i) => ({ ...p, sort_order: i }));
    });
  };

  // --- save -------------------------------------------------------------
  const buildPayload = () => {
    const cleanedJobs = jobs.map((row) => ({
      ...(row.id ? { id: row.id } : {}),
      job_id: row.job?.id ?? row.job_id,
      quantity: row.quantity === '' || row.quantity === null || row.quantity === undefined
        ? null : row.quantity,
      description_override: row.description_override || '',
      is_optional: !!row.is_optional,
      sort_order: row.sort_order,
    }));
    const cleanedParts = parts.map((row) => ({
      ...(row.id ? { id: row.id } : {}),
      part_id: row.part?.id ?? row.part_id,
      quantity: row.quantity === '' ? '1' : row.quantity,
      alternative_group: row.alternative_group,
      is_preferred: !!row.is_preferred,
      sort_order: row.sort_order,
    }));
    return {
      name,
      description,
      is_active: isActive,
      estimated_labor_hours: estimatedLaborHours === '' ? null : estimatedLaborHours,
      jobs: cleanedJobs,
      parts: cleanedParts,
    };
  };

  const validate = () => {
    if (!name.trim()) return t('huoltopaketit.errorNameRequired');
    if ([...jobs, ...parts].some((row) => row._status === 'missing')) {
      return t('huoltopaketit.errorMissingRows');
    }
    for (const j of jobs) if (!j.job?.id) return t('huoltopaketit.errorJobRequired');
    for (const p of parts) if (!p.part?.id) return t('huoltopaketit.errorPartRequired');
    return null;
  };

  // The backend resolves lines whose part was deleted from the register (same-code
  // register part, then vendor catalogues). Rows come back as ok / replaced / missing;
  // missing rows block save until the user picks a replacement or removes them.
  // Errors propagate: the copy dialog shows them.
  const handleCopyFromWorkOrder = async ({ maintenance: sourceMaintenance, copyParts: doParts, copyJobs: doJobs }) => {
    const response = await axios.post(
      '/api/service-packages/from-work-order/',
      { maintenance_id: sourceMaintenance.id },
      { params: { organization: orgId } },
    );
    const data = response.data;
    const stamp = Date.now();
    const jobRows = doJobs ? (data.jobs || []) : [];
    const partRows = doParts ? (data.parts || []) : [];

    if (jobRows.length) {
      setJobs((prev) => [
        ...prev,
        ...jobRows.map((row, i) => ({
          _key: `new-${stamp}-${Math.random()}-j${i}`,
          job: row.job,
          job_id: row.job_id,
          quantity: row.quantity ?? '',
          description_override: '',
          is_optional: false,
          sort_order: prev.length + i,
          _status: row.status === 'ok' ? undefined : row.status,
          _original: row.original || undefined,
        })),
      ]);
    }

    if (partRows.length) {
      setParts((prev) => [
        ...prev,
        ...partRows.map((row, i) => ({
          _key: `new-${stamp}-${Math.random()}-p${i}`,
          part: row.part,
          part_id: row.part_id,
          quantity: row.quantity ?? '1',
          alternative_group: null,
          is_preferred: false,
          sort_order: prev.length + i,
          _status: row.status === 'ok' ? undefined : row.status,
          _basis: row.basis || undefined,
          _original: row.original || undefined,
          _candidates: row.candidates || [],
        })),
      ]);
    }

    const missing = [...jobRows, ...partRows].filter((row) => row.status === 'missing').length;
    const replaced = partRows.filter((row) => row.status === 'replaced').length;
    if (missing) {
      setSnackbar({ severity: 'warning', message: t('huoltopaketit.copyMissing', { count: missing }) });
    } else if (replaced) {
      setSnackbar({ severity: 'info', message: t('huoltopaketit.copyReplaced', { count: replaced }) });
    }
  };

  const openReplacePartPicker = (row) => {
    setAltSourceKey(null);
    setReplacePartKey(row._key);
    setPartDialogSearch(row._original?.manufacturer_code || row._original?.name || '');
    setAddPartDialogOpen(true);
  };

  const openReplaceJobPicker = (row) => {
    setReplaceJobKey(row._key);
    setAddJobDialogOpen(true);
  };

  const candidatesRow = candidatesRowKey ? parts.find((p) => p._key === candidatesRowKey) : null;

  // User chose another stand-in for a replaced row: swap the part, keep the row flagged
  // so the chip still tells where the part came from, remember the choice in the list.
  const handleCandidatePicked = (candidate, livePart) => {
    setParts((prev) => prev.map((row) => {
      if (row._key !== candidatesRowKey) return row;
      return {
        ...row,
        part: {
          id: livePart.id,
          name: livePart.name,
          manufacturer_code: livePart.manufacturer_code || '',
          manufacturer_name: livePart.manufacturer_name || '',
          unit: livePart.unit || 'kpl',
          primary_oe_number: livePart.primary_identifier_code || '',
        },
        part_id: livePart.id,
        _basis: candidate.kind,
        _candidates: (row._candidates || []).map((c) => {
          const isPicked = c === candidate;
          return { ...c, selected: isPicked, part_id: isPicked ? livePart.id : c.part_id };
        }),
      };
    }));
    setCandidatesRowKey(null);
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      setSnackbar({ severity: 'warning', message: err });
      return;
    }
    setSaving(true);
    setRowErrors({ jobs: {}, parts: {} });
    try {
      const payload = buildPayload();
      if (isNew) {
        const response = await axios.post(
          `/api/service-packages/?organization=${orgId}`,
          { ...payload, organization: orgId },
        );
        navigate(`/huoltopaketit/${response.data.id}`, { replace: true });
        setSnackbar({ severity: 'success', message: t('huoltopaketit.saved') });
      } else {
        await axios.patch(
          `/api/service-packages/${id}/`,
          payload,
          { params: { organization: orgId } },
        );
        setSnackbar({ severity: 'success', message: t('huoltopaketit.saved') });
      }
    } catch (error) {
      // Nested DRF errors come back per row ({"parts": [{}, {"part_id": [...]}, ...]}).
      // Pin them to the rows so the user sees which line is wrong, not just "failed".
      let detail = t('huoltopaketit.errorSaving');
      const data = error.response?.data;
      const nextErrors = { jobs: {}, parts: {} };
      let rowCount = 0;
      const collect = (list, rows, bucket) => {
        if (!Array.isArray(list)) return;
        list.forEach((err, i) => {
          const messages = flattenErrors(err);
          if (messages.length && rows[i]) {
            bucket[rows[i]._key] = messages.join(' ');
            rowCount += 1;
          }
        });
      };
      if (typeof data === 'string') {
        detail = data;
      } else if (data && typeof data === 'object') {
        collect(data.jobs, jobs, nextErrors.jobs);
        collect(data.parts, parts, nextErrors.parts);
        if (data.detail) {
          detail = flattenErrors(data.detail).join(' ');
        } else if (data.non_field_errors) {
          detail = flattenErrors(data.non_field_errors).join(', ');
        } else if (rowCount) {
          detail = t('huoltopaketit.errorRows', { count: rowCount });
        } else {
          const first = flattenErrors(data);
          if (first.length) detail = first[0];
        }
      }
      setRowErrors(nextErrors);
      setSnackbar({ severity: 'error', message: detail });
    } finally {
      setSaving(false);
    }
  };

  const partsByGroup = useMemo(() => {
    const groups = new Map();
    parts.forEach((p) => {
      if (!p.alternative_group) return;
      if (!groups.has(p.alternative_group)) groups.set(p.alternative_group, []);
      groups.get(p.alternative_group).push(p._key);
    });
    return groups;
  }, [parts]);

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pb: 12 }} data-testid="service-package-edit-page">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/huoltopaketit')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {isNew ? t('huoltopaketit.new') : t('huoltopaketit.editTitle')}
        </Typography>
      </Box>

      {/* Header card */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' } }}>
          <TextField
            label={t('huoltopaketit.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            required
            fullWidth
          />
          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label={t('huoltopaketit.isActive')}
          />
        </Box>
        <TextField
          label={t('huoltopaketit.description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
        />
        <Box sx={{ mt: 2, maxWidth: 320 }}>
          <DecimalField
            label={t('huoltopaketit.estimatedLaborHours')}
            value={estimatedLaborHours}
            onChange={(v) => setEstimatedLaborHours(v)}
            maxDecimals={2}
            fullWidth
            size="small"
            placeholder={computedJobHours || ''}
          />
          {computedJobHours && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {t('huoltopaketit.computedFromJobs', { hours: computedJobHours })}
            </Typography>
          )}
        </Box>
      </Paper>

      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={() => setCopyFromDialogOpen(true)}
          sx={{
            borderColor: 'divider',
            color: 'text.secondary',
            borderRadius: '10px',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': { borderColor: 'text.primary', bgcolor: 'background.paper' },
          }}
        >
          {t('copyFromMaintenance.pickTitle')}
        </Button>
      </Box>

      {/* Jobs section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{t('huoltopaketit.jobs')}</Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAddJobDialogOpen(true)}
            data-testid="add-template-job-button"
          >
            {t('huoltopaketit.addJob')}
          </Button>
        </Box>
        {jobs.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('huoltopaketit.noJobs')}
          </Typography>
        )}
        {jobs.map((row, idx) => {
          const j = row.job || {};
          const quantityLabel = j.price_type === 'hourly'
            ? t('huoltopaketit.hours')
            : t('huoltopaketit.quantity');
          return (
            <Box
              key={row._key}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr auto auto auto' },
                gap: 1.5,
                alignItems: 'center',
                mb: 1.5,
                py: 1,
                borderTop: idx > 0 ? 1 : 0,
                borderColor: 'divider',
              }}
            >
              <Box>
                {row._status === 'missing' ? (
                  <>
                    <Typography fontWeight={500} color="text.secondary">{row._original?.name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                      <Chip size="small" color="warning" label={t('huoltopaketit.jobDeleted')} data-testid="job-deleted-chip" />
                      <Button size="small" onClick={() => openReplaceJobPicker(row)} data-testid={`pick-replacement-job-${row._key}`}>
                        {t('huoltopaketit.pickReplacement')}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography fontWeight={500}>{j.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {j.price_type === 'hourly'
                        ? `${j.price} €/h`
                        : `${j.price} €`}
                    </Typography>
                  </>
                )}
                {rowErrors.jobs[row._key] && (
                  <Typography variant="caption" color="error" sx={{ display: 'block' }} data-testid="job-row-error">
                    {rowErrors.jobs[row._key]}
                  </Typography>
                )}
              </Box>
              <DecimalField
                label={quantityLabel}
                value={row.quantity ?? ''}
                onChange={(v) => updateJob(row._key, { quantity: v })}
                maxDecimals={2}
                size="small"
                fullWidth
              />
              <TextField
                label={t('huoltopaketit.descriptionOverride')}
                value={row.description_override || ''}
                onChange={(e) => updateJob(row._key, { description_override: e.target.value })}
                placeholder={j.name}
                size="small"
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={Boolean(row.is_optional)}
                    onChange={(e) => updateJob(row._key, { is_optional: e.target.checked })}
                  />
                }
                label={t('huoltopaketit.isOptional')}
                sx={{ m: 0 }}
              />
              <Box sx={{ display: 'flex' }}>
                <IconButton size="small" onClick={() => moveJob(row._key, -1)} disabled={idx === 0}>
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => moveJob(row._key, 1)} disabled={idx === jobs.length - 1}>
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </Box>
              <IconButton size="small" onClick={() => removeJob(row._key)} color="error">
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
      </Paper>

      {/* Parts section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{t('huoltopaketit.parts')}</Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => { setAltSourceKey(null); setAddPartDialogOpen(true); }}
            data-testid="add-template-part-button"
          >
            {t('huoltopaketit.addPart')}
          </Button>
        </Box>
        {parts.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('huoltopaketit.noParts')}
          </Typography>
        )}
        {parts.map((row, idx) => {
          const p = row.part || {};
          const inGroup = Boolean(row.alternative_group);
          const groupKeys = inGroup ? partsByGroup.get(row.alternative_group) : null;
          const isFirstInGroup = inGroup && groupKeys && groupKeys[0] === row._key;
          return (
            <Box key={row._key}>
              {isFirstInGroup && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    pl: 2,
                    pt: idx > 0 ? 1.5 : 0,
                    pb: 0.5,
                    color: 'text.secondary',
                    fontWeight: 600,
                    borderLeft: '4px solid',
                    borderColor: 'secondary.main',
                  }}
                >
                  {t('huoltopaketit.altGroupHeader', { count: groupKeys.length })}
                </Typography>
              )}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '3fr 1fr auto auto auto' },
                gap: 1.5,
                alignItems: 'center',
                mb: inGroup && groupKeys && groupKeys[groupKeys.length - 1] === row._key ? 1.5 : 0,
                py: 1,
                pl: inGroup ? 2 : 0,
                borderLeft: inGroup ? '4px solid' : 'none',
                borderColor: 'secondary.main',
                borderTop: idx > 0 && !isFirstInGroup ? 1 : 0,
                borderTopColor: 'divider',
              }}
            >
              <Box>
                {row._status === 'missing' ? (
                  <>
                    <Typography fontWeight={500} color="text.secondary">{row._original?.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[row._original?.manufacturer_code, row._original?.manufacturer_name].filter(Boolean).join(' · ')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                      <Chip size="small" color="warning" label={t('huoltopaketit.partDeleted')} data-testid="part-deleted-chip" />
                      <Button size="small" onClick={() => openReplacePartPicker(row)} data-testid={`pick-replacement-part-${row._key}`}>
                        {t('huoltopaketit.pickReplacement')}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography fontWeight={500}>{p.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[p.manufacturer_code, p.manufacturer_name].filter(Boolean).join(' · ')}
                      {p.primary_oe_number && <> · OE {p.primary_oe_number}</>}
                    </Typography>
                    {row._status === 'replaced' && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                        <Tooltip title={t('huoltopaketit.replacedOriginal', { name: row._original?.name || '' })}>
                          <Chip
                            size="small"
                            color="info"
                            variant="outlined"
                            label={(row._candidates?.length || 0) > 1
                              ? t('huoltopaketit.replacedWithOptions', { count: row._candidates.length })
                              : row._basis === 'catalogue'
                                ? t('huoltopaketit.partReplacedCatalogue')
                                : t('huoltopaketit.partReplacedRegister')}
                            data-testid="part-replaced-chip"
                          />
                        </Tooltip>
                        {(row._candidates?.length || 0) > 1 && (
                          <Button
                            size="small"
                            onClick={() => setCandidatesRowKey(row._key)}
                            data-testid={`switch-replacement-${row._key}`}
                          >
                            {t('huoltopaketit.switchReplacement')}
                          </Button>
                        )}
                      </Box>
                    )}
                  </>
                )}
                {rowErrors.parts[row._key] && (
                  <Typography variant="caption" color="error" sx={{ display: 'block' }} data-testid="part-row-error">
                    {rowErrors.parts[row._key]}
                  </Typography>
                )}
              </Box>
              <DecimalField
                label={t('huoltopaketit.quantity')}
                value={row.quantity ?? '1'}
                onChange={(v) => updatePart(row._key, { quantity: v })}
                maxDecimals={2}
                size="small"
                fullWidth
                helperText={p.unit}
              />
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {inGroup ? (
                  <Tooltip title={row.is_preferred ? t('huoltopaketit.preferred') : t('huoltopaketit.markPreferred')}>
                    <IconButton size="small" onClick={() => markPreferred(row._key)}>
                      {row.is_preferred
                        ? <StarIcon fontSize="small" color="secondary" />
                        : <StarBorderIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                ) : null}
                <Tooltip title={t('huoltopaketit.addAlternativePart')}>
                  <IconButton
                    size="small"
                    onClick={() => { setAltSourceKey(row._key); setAddPartDialogOpen(true); }}
                    data-testid={`add-alt-part-${row._key}`}
                  >
                    <CallSplitIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {inGroup && (
                  <Tooltip title={t('huoltopaketit.removeFromAltGroup')}>
                    <IconButton size="small" onClick={() => removeFromAltGroup(row._key)}>
                      <LinkOffIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ display: 'flex' }}>
                <IconButton size="small" onClick={() => movePart(row._key, -1)} disabled={idx === 0}>
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => movePart(row._key, 1)} disabled={idx === parts.length - 1}>
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </Box>
              <IconButton size="small" onClick={() => removePart(row._key)} color="error">
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
            </Box>
          );
        })}
      </Paper>

      {/* Sticky save bar */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: { xs: 0, md: 240 },
          right: 0,
          pl: 3,
          pr: { xs: 11, sm: 11 },
          py: 2,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
          zIndex: 10,
        }}
      >
        <Button variant="outlined" onClick={() => navigate('/huoltopaketit')} disabled={saving}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          data-testid="service-package-save-button"
        >
          {saving ? <CircularProgress size={20} /> : t('Save')}
        </Button>
      </Box>

      {/* Reused dialogs in registry mode */}
      <AddPartDialog
        open={addPartDialogOpen}
        handleClose={() => {
          setAddPartDialogOpen(false);
          setAltSourceKey(null);
          setReplacePartKey(null);
          setPartDialogSearch('');
        }}
        mode="template"
        initialSearch={partDialogSearch}
        onPartCreated={handlePartPicked}
        // These are unused in registry mode but some internal paths still read them;
        // provide safe placeholders.
        setMaintenance={() => {}}
        maintenance={null}
      />
      <AddJobDialog
        open={addJobDialogOpen}
        handleClose={() => { setAddJobDialogOpen(false); setReplaceJobKey(null); }}
        mode="template"
        onJobCreated={handleJobPicked}
        setMaintenance={() => {}}
        maintenance={null}
      />

      <CopyFromMaintenanceDialog
        open={copyFromDialogOpen}
        handleClose={() => setCopyFromDialogOpen(false)}
        mode="pick"
        onPick={handleCopyFromWorkOrder}
      />

      <ReplacementCandidatesDialog
        open={Boolean(candidatesRow)}
        onClose={() => setCandidatesRowKey(null)}
        row={candidatesRow}
        onPick={handleCandidatePicked}
      />

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        // A click elsewhere (e.g. Save while the copy summary is still showing) must not
        // wipe the message that click just produced.
        onClose={(_event, reason) => { if (reason !== 'clickaway') setSnackbar(null); }}
      >
        {snackbar && <Alert severity={snackbar.severity}>{snackbar.message}</Alert>}
      </Snackbar>
    </Box>
  );
}

export default ServicePackageEditView;
